import React, { useState, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { motion, AnimatePresence } from "framer-motion";
import { themeAtom, isAdminAtom, listsAtom } from "../../../../-Atoms";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Paper from "@mui/material/Paper";

import ImagesChip from "../../../../GlobalComponents/ImagesChip";
import DueDateChip from "../../../../GlobalComponents/DueDateChip";
import LabelMenuV2 from "../common/LabelMenuV2";
import LabelChip from "../../../../GlobalComponents/LabelChip";
import PermanentLabelChip from "../../../../GlobalComponents/PermanentLabelChip";
import ListSelector from "./ListSelector";
import Invoices from "./Invoices";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";

// Import tab components
import ShootInfoTab from "./ShootInfoTab";
import InvoicingTab from "./InvoicingTab";
import PeopleTab from "./PeopleTab";
import ImageSpecsTab from "./ImageSpecsTab";
import OutsourcersTab from "./OutsourcersTab";
import ChecklistsV2 from "./ChecklistsV2";
import URLModalV2 from "./URLModalV2";
import StartShootingButton from "./StartShootingButton";

const JobTab = ({ jobData, clientDataInit, setHasUnsavedChanges, allMultipleDayJobs = [] }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [lists] = useAtom(listsAtom);

  const [activeTab, setActiveTab] = useState("shootInfo");
  const [clientData, setClientData] = useState(clientDataInit);

  // Due date state - now handled by DueDateChip

  // List state
  const [idList, setIdList] = useState("");

  // Refs
  const labelsContainer = useRef(null);

  // Label menu state
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);

  // URL Modal state for ChecklistsV2
  const [urlModalUrl, setUrlModalUrl] = useState("");
  const [URLModalOpen, setURLModalOpen] = useState(false);

  useEffect(() => {
    setClientData(clientDataInit);
  }, [clientDataInit]);

  // Effect to determine which list the job is in
  useEffect(() => {
    let tmpId = "";
    lists?.forEach((list) => {
      if (list.items?.indexOf(jobData.id) !== -1) {
        if (!tmpId) {
          tmpId = list.id;
          setIdList(list.id);
        } else {
          alert(
            "List Conflict: Job is in 2 Lists, this shouldn't be possible. Please screenshot this (including the job number) and send it to Alex. The first list to be found is what will be used."
          );
        }
      }
    });

    return () => {
      setIdList("");
    };
  }, [lists, jobData]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const tabVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 1,
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ width: "100%", position: "relative" }}>
        <Paper
          elevation={0}
          sx={{
            position: "sticky",
            top: 112,
            zIndex: 50,
            backgroundColor:
              theme === "dark"
                ? "rgba(28, 28, 28, 0.95)"
                : "rgba(250, 250, 250, 0.95)",
            backdropFilter: "blur(10px)",
            marginBottom: 1,
            marginLeft: -1,
            marginRight: -1,
            marginTop: 0,
            paddingTop: 1,
            paddingLeft: 2,
            paddingRight: 2,
            borderRadius: "0px !important",
          }}
        >
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              selectionFollowsFocus
              sx={{
                borderRadius: "8px",
                background:
                  theme === "dark"
                    ? "rgba(50, 50, 50, 0.25)"
                    : "rgba(230, 230, 230, 0.6)",
                backdropFilter: "blur(10px)",
                minHeight: "48px",
                "& .MuiTabs-flexContainer": {
                  justifyContent: { xs: "flex-start", sm: "center" },
                },
                "& .MuiTabs-scroller": {
                  overflow: "auto !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme === "dark" ? "#4db6ac" : "#00897b",
                  height: "2px",
                  borderRadius: "2px 2px 0 0",
                },
                "& .MuiTab-root": {
                  minHeight: "48px",
                  padding: "10px 24px",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  textTransform: "none",
                  color:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.6)"
                      : "rgba(0, 0, 0, 0.6)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  minWidth: { xs: "0", sm: "0" },
                  flex: { xs: "0 0 auto", sm: "1 1 auto" },
                  whiteSpace: "nowrap",
                  "&:hover": {
                    color: theme === "dark" ? "#ffffff" : "#000000",
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)",
                  },
                  "&.Mui-selected": {
                    color: theme === "dark" ? "#80cbc4" : "#00695c",
                    fontWeight: 700,
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(77, 182, 172, 0.08)"
                        : "rgba(0, 137, 123, 0.1)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "2px",
                      background: theme === "dark" ? "#4db6ac" : "#00897b",
                      borderRadius: "2px 2px 0 0",
                    },
                  },
                },
                "& .MuiTabs-scrollButtons": {
                  color:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(0, 0, 0, 0.3)",
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab label="Shoot Info" value="shootInfo" />
              <Tab label="Invoicing" value="invoicing" />
              <Tab label="People" value="people" />
              <Tab label="Image Specs" value="imageSpecs" />
              {(jobData?.idList !== "5f081763cd97556d03075c86" || isAdmin) && (
                <Tab label="Outsourcers" value="outsourcers" />
              )}
            </Tabs>
          </Box>
        </Paper>

        {/* Start Shooting Button - appears when job is not in any list */}
        <StartShootingButton
          jobData={jobData}
          clientData={clientData}
          idList={idList}
          setHasUnsavedChanges={setHasUnsavedChanges}
          allMultipleDayJobs={allMultipleDayJobs}
        />

        {/* Container for Due Date, Images Chip, and LabelMenu */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Box sx={{ px: 2, mb: 3 }}>
            {/* Labels section with due dates and images chip on the left */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                mt: 2.5,
                mb: 2.5,
                padding: 1.5,
                borderRadius: 2,
                backgroundColor:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(0, 0, 0, 0.02)",
                border: `1px solid ${
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.08)"
                }`,
                position: "relative",
              }}
            >
              {/* Images chip - separate container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  flexShrink: 0,
                }}
              >
                <ImagesChip jobData={jobData} clientData={clientData} />
              </Box>

              {/* Due dates container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  alignItems: "flex-start",
                  flexShrink: 0,
                }}
              >
                <DueDateChip
                  dueDate={jobData.dueDate}
                  complete={jobData.complete}
                  expressDueDate={jobData.expressDueDate}
                  expressComplete={jobData.expressComplete}
                  id={jobData.id}
                  theme={theme}
                  canEdit={true}
                  flexAlign="flex-start"
                />
              </Box>

              {/* Labels section in the middle */}
              <Box
                ref={labelsContainer}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  flex: 1,
                  minHeight: "32px",
                  marginRight: "60px", // Reserve space for the add button
                }}
              >
                {/* Permanent labels */}
                {jobData.idLabelsPermanent &&
                  jobData.idLabelsPermanent.map((label, index) => (
                    <PermanentLabelChip
                      key={`permanent-${label}-${index}`}
                      label={label}
                      kanbanBoard={false}
                      jobData={jobData}
                      setLabelMenuOpen={setLabelMenuOpen}
                    />
                  ))}

                {/* Regular labels */}
                {jobData.idLabels &&
                  jobData.idLabels.map((label, index) => (
                    <LabelChip
                      key={`regular-${label}-${index}`}
                      label={label}
                      setLabelMenuOpen={setLabelMenuOpen}
                      kanbanBoard={false}
                    />
                  ))}

                <LabelMenuV2
                  jobData={jobData}
                  open={labelMenuOpen}
                  setOpen={setLabelMenuOpen}
                />
              </Box>

              {/* Add label button - positioned on the right */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 16,
                  transform: "translateY(-50%)",
                }}
              >
                <IconButton
                  onClick={() => setLabelMenuOpen(true)}
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)",
                    border: `1px solid ${
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)"
                    }`,
                    color:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.7)"
                        : "rgba(0, 0, 0, 0.7)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)",
                      borderColor:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.9)"
                          : "rgba(0, 0, 0, 0.9)",
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {/* List Selector and Invoices with spacing */}
            <Box sx={{ mt: 1.5 }}>
              <ListSelector
                jobData={jobData}
                idList={idList}
                setIdList={setIdList}
              />
            </Box>
            {isAdmin ? (
              <Box sx={{ mt: 1.5 }}>
                <Invoices jobData={jobData} />
              </Box>
            ) : null}
          </Box>
        </motion.div>

        <Box
          sx={{
            px: 1,
            minHeight: "800px",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={tabVariants}
              style={{ width: "100%" }}
            >
              {activeTab === "shootInfo" && (
                <ShootInfoTab
                  jobData={jobData}
                  clientData={clientData}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                  setUrlModalUrl={setUrlModalUrl}
                  setURLModalOpen={setURLModalOpen}
                  idList={idList}
                />
              )}

              {activeTab === "invoicing" && (
                <InvoicingTab
                  jobData={jobData}
                  clientData={clientData}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
              )}

              {activeTab === "people" && (
                <PeopleTab
                  jobData={jobData}
                  clientData={clientData}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
              )}

              {activeTab === "imageSpecs" && (
                <ImageSpecsTab
                  jobData={jobData}
                  clientData={clientData}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
              )}

              {activeTab === "outsourcers" && (
                <OutsourcersTab
                  jobData={jobData}
                  clientData={clientData}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </Box>

        {/* ChecklistsV2 - Visible on all tabs underneath tab content */}
        <Box sx={{ px: 1, mb: 4 }}>
          <ChecklistsV2
            jobData={jobData}
            clientData={clientData}
            setUrlModalUrl={setUrlModalUrl}
            setURLModalOpen={setURLModalOpen}
            idList={idList}
          />
        </Box>

        {/* URLModalV2 for viewing attachments and checklist images */}
        <URLModalV2
          open={URLModalOpen}
          handleClose={() => setURLModalOpen(false)}
          url={urlModalUrl}
        />
      </Box>
    </motion.div>
  );
};

export default JobTab;
